import { useEffect, useState } from 'react'
import { throttle } from 'throttle-debounce'
// https://github.com/streamich/react-use/blob/master/src/misc/util.ts

function on<T extends Window | Document | HTMLElement | EventTarget>(
	obj: T | null,
	// eslint-disable-next-line @typescript-eslint/ban-types
	...args: Parameters<T['addEventListener']> | [string, Function | null, ...any]
): void {
	if (obj && obj.addEventListener) {
		obj.addEventListener(
			...(args as Parameters<HTMLElement['addEventListener']>),
		)
	}
}

function off<T extends Window | Document | HTMLElement | EventTarget>(
	obj: T | null,
	...args:
		| Parameters<T['removeEventListener']>
		// eslint-disable-next-line @typescript-eslint/ban-types
		| [string, Function | null, ...any]
): void {
	if (obj && obj.removeEventListener) {
		obj.removeEventListener(
			...(args as Parameters<HTMLElement['removeEventListener']>),
		)
	}
}

const defaultEvents = [
	'mousemove',
	'mousedown',
	'resize',
	'keydown',
	'touchstart',
	'wheel',
]
const oneMinute = 60e3

const useIdle = (
	ms: number = oneMinute,
	initialState = false,
	events: string[] = defaultEvents,
): boolean => {
	const [state, setState] = useState<boolean>(initialState)

	useEffect(() => {
		let mounted = true
		let timeout: any
		let localState: boolean = state
		const set = (newState: boolean) => {
			if (mounted) {
				localState = newState
				setState(newState)
			}
		}

		const onEvent = throttle(50, () => {
			if (localState) {
				set(false)
			}

			clearTimeout(timeout)
			timeout = setTimeout(() => set(true), ms)
		})
		const onVisibility = () => {
			if (!document.hidden) {
				onEvent()
			}
		}

		for (let i = 0; i < events.length; i++) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			on(window, events[i], onEvent)
		}
		on(document, 'visibilitychange', onVisibility)

		timeout = setTimeout(() => set(true), ms)

		return () => {
			mounted = false

			for (let i = 0; i < events.length; i++) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				off(window, events[i], onEvent)
			}
			off(document, 'visibilitychange', onVisibility)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ms, events])

	return state
}

export default useIdle
